// Plan des emplacements d'un marché (écran S12) : le plan JSON de l'API (unité : mètre) rendu en
// SVG, zones, éléments de repère, cotes, emplacements colorés par situation (disponible, attribué,
// expire sous 30 jours, réservé, hors service), sélection au clic et au clavier (Entrée ou Espace,
// flèches pour passer d'un emplacement au voisin), légende textuelle. La couleur n'est jamais la
// seule information : la référence, la marque et le libellé accessible la portent aussi.
import type { PlanMarche as Plan } from '@cc/contrats';
import { useMemo, useRef, type KeyboardEvent } from 'react';
import { aujourdhuiIso, LIBELLES_SITUATION_EMPLACEMENT, MARQUES_SITUATION, situationEmplacement, type FiltreEmplacements, type SituationEmplacement } from './logique';
import { correspondFiltre } from './logique';
import type { Emplacement, ZoneMarche } from './types';
import styles from './marches.module.css';

export interface ProprietesPlanMarche {
  plan: Plan;
  emplacements: Emplacement[];
  zones: ZoneMarche[];
  selection: string | null;
  onSelection: (id: string) => void;
  /** Filtre de l'écran : les emplacements qui n'y correspondent pas sont estompés, jamais retirés. */
  filtre?: FiltreEmplacements;
  /** Zone mise en avant (les autres sont estompées). */
  zoneActive?: string | null;
  aujourdhui?: string;
}

interface Case {
  e: Emplacement;
  x: number;
  y: number;
  largeur: number;
  hauteur: number;
  rotation: number;
  situation: SituationEmplacement;
  estompe: boolean;
}

const LIBELLES_ELEMENT: Record<string, string> = { allee: 'Allée', batiment: 'Bâtiment', entree: 'Entrée', sanitaires: 'Sanitaires', parking: 'Parking', autre: '' };

/** Libellé accessible d'une case : « B-03, attribué à Henriette Bouanga, jusqu'au 2026-12-31 ». */
export function libelleCase(e: Emplacement, situation: SituationEmplacement): string {
  const base = `${e.reference}, ${LIBELLES_SITUATION_EMPLACEMENT[situation].toLowerCase()}`;
  if ((situation === 'attribue' || situation === 'expire_bientot') && e.attribution_en_cours) {
    return `${base}, ${e.attribution_en_cours.commercant}${e.attribution_en_cours.fin ? `, jusqu'au ${e.attribution_en_cours.fin}` : ''}`;
  }
  if ((situation === 'reserve' || situation === 'hors_service') && e.etat_motif) return `${base} : ${e.etat_motif}`;
  return base;
}

/** Case voisine dans la direction d'une flèche : la plus proche dont le centre est de ce côté. */
export function voisine(cases: Case[], depuis: Case, touche: string): Case | null {
  const cx = depuis.x + depuis.largeur / 2;
  const cy = depuis.y + depuis.hauteur / 2;
  const candidates = cases.filter((c) => {
    if (c === depuis) return false;
    const dx = c.x + c.largeur / 2 - cx;
    const dy = c.y + c.hauteur / 2 - cy;
    if (touche === 'ArrowRight') return dx > 0.01 && Math.abs(dy) <= Math.abs(dx);
    if (touche === 'ArrowLeft') return dx < -0.01 && Math.abs(dy) <= Math.abs(dx);
    if (touche === 'ArrowDown') return dy > 0.01 && Math.abs(dx) <= Math.abs(dy);
    if (touche === 'ArrowUp') return dy < -0.01 && Math.abs(dx) <= Math.abs(dy);
    return false;
  });
  let meilleure: Case | null = null;
  let distance = Infinity;
  for (const c of candidates) {
    const d = (c.x + c.largeur / 2 - cx) ** 2 + (c.y + c.hauteur / 2 - cy) ** 2;
    if (d < distance) {
      distance = d;
      meilleure = c;
    }
  }
  return meilleure;
}

export function PlanMarche({ plan, emplacements, zones, selection, onSelection, filtre = 'tous', zoneActive = null, aujourdhui = aujourdhuiIso() }: ProprietesPlanMarche) {
  const refs = useRef<Record<string, SVGGElement | null>>({});
  const parReference = useMemo(() => new Map(emplacements.map((e) => [e.reference, e])), [emplacements]);
  const nomsZones = useMemo(() => new Map(zones.map((z) => [z.code, z.nom])), [zones]);
  const cases = useMemo<Case[]>(
    () =>
      plan.emplacements.flatMap((p) => {
        const e = parReference.get(p.reference);
        if (!e) return [];
        const situation = situationEmplacement(e, aujourdhui);
        const horsZone = Boolean(zoneActive) && e.zone.code !== zoneActive;
        return [{ e, x: p.x, y: p.y, largeur: p.largeur, hauteur: p.hauteur, rotation: p.rotation, situation, estompe: horsZone || !correspondFiltre(e, filtre, aujourdhui) }];
      }),
    [plan, parReference, filtre, zoneActive, aujourdhui],
  );
  // Taille des textes en unités du plan : lisible quel que soit le format du marché.
  const unite = Math.max(0.6, Math.min(plan.largeur, plan.hauteur) / 40);
  const nonPlaces = emplacements.filter((e) => !plan.emplacements.some((p) => p.reference === e.reference));

  const clavier = (ev: KeyboardEvent<SVGGElement>, c: Case) => {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      onSelection(c.e.id);
      return;
    }
    if (ev.key.startsWith('Arrow')) {
      const v = voisine(cases, c, ev.key);
      if (v) {
        ev.preventDefault();
        refs.current[v.e.id]?.focus();
      }
    }
  };

  return (
    <div className={styles.pile}>
      <svg
        className={styles.plan}
        viewBox={`-${unite} -${unite * 2} ${plan.largeur + unite * 2} ${plan.hauteur + unite * 3}`}
        preserveAspectRatio="xMidYMid meet"
        role="group"
        aria-label={`Plan des emplacements : ${cases.length} emplacement${cases.length > 1 ? 's' : ''} placé${cases.length > 1 ? 's' : ''}, ${plan.largeur} m sur ${plan.hauteur} m`}
      >
        {plan.zones.map((z) => (
          <g key={z.code} className={zoneActive && z.code !== zoneActive ? styles.zoneEstompee : undefined}>
            <rect className={styles.zone} x={z.x} y={z.y} width={z.largeur} height={z.hauteur} rx={unite / 4} />
            <text className={styles.libelleZone} x={z.x + unite / 3} y={z.y - unite / 4} fontSize={unite * 0.8}>
              Zone {z.code}
              {nomsZones.get(z.code) ? ` · ${nomsZones.get(z.code)}` : ''}
            </text>
          </g>
        ))}
        {plan.elements.map((el, i) => (
          <g key={`${el.type}-${i}`}>
            <rect className={styles.element} x={el.x} y={el.y} width={el.largeur} height={el.hauteur} />
            <text className={styles.libelleElement} x={el.x + el.largeur / 2} y={el.y + el.hauteur / 2} fontSize={unite * 0.7} textAnchor="middle" dominantBaseline="middle">
              {el.libelle ?? LIBELLES_ELEMENT[el.type] ?? el.type}
            </text>
          </g>
        ))}
        {plan.cotes.map((c, i) => (
          <g key={`cote-${i}`}>
            <line className={styles.ligneCote} x1={c.de.x} y1={c.de.y} x2={c.a.x} y2={c.a.y} />
            <text className={styles.libelleCote} x={(c.de.x + c.a.x) / 2} y={(c.de.y + c.a.y) / 2 - unite * 0.3} fontSize={unite * 0.7} textAnchor="middle">
              {c.libelle}
            </text>
          </g>
        ))}
        {cases.map((c) => {
          const marque = MARQUES_SITUATION[c.situation];
          const cx = c.x + c.largeur / 2;
          const cy = c.y + c.hauteur / 2;
          return (
            <g
              key={c.e.id}
              ref={(el) => {
                refs.current[c.e.id] = el;
              }}
              className={styles.emplacement}
              data-situation={c.situation}
              data-estompe={c.estompe || undefined}
              role="button"
              tabIndex={0}
              aria-label={libelleCase(c.e, c.situation)}
              aria-pressed={selection === c.e.id}
              transform={c.rotation ? `rotate(${c.rotation} ${cx} ${cy})` : undefined}
              onClick={() => onSelection(c.e.id)}
              onKeyDown={(ev) => clavier(ev, c)}
            >
              <rect x={c.x} y={c.y} width={c.largeur} height={c.hauteur} rx={unite / 5} />
              <text className={styles.reference} x={cx} y={marque ? cy - unite * 0.25 : cy} fontSize={Math.min(unite * 0.8, c.largeur / 3.2)} textAnchor="middle" dominantBaseline="middle">
                {c.e.reference}
              </text>
              {marque ? (
                <text className={styles.marque} x={cx} y={cy + unite * 0.55} fontSize={Math.min(unite * 0.6, c.largeur / 4)} textAnchor="middle" dominantBaseline="middle">
                  {marque}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      <ul className={styles.legende} aria-label="Légende du plan">
        {(['attribue', 'expire_bientot', 'disponible', 'reserve', 'hors_service'] as const).map((s) => (
          <li key={s}>
            <span className={styles.echantillon} data-situation={s} aria-hidden /> {LIBELLES_SITUATION_EMPLACEMENT[s]}
            {MARQUES_SITUATION[s] ? ` (${MARQUES_SITUATION[s]})` : ''}
          </li>
        ))}
      </ul>
      {nonPlaces.length > 0 ? (
        <p className={styles.aide}>
          {nonPlaces.length} emplacement{nonPlaces.length > 1 ? 's' : ''} enregistré{nonPlaces.length > 1 ? 's' : ''} sans position sur le plan : {nonPlaces.map((e) => e.reference).join(', ')}.
        </p>
      ) : null}
    </div>
  );
}
